import { AuthFooterLink, AuthMessage, Button } from '@protoss/ui';
import * as React from 'react';
import { AuthCard, AuthHeader, AuthLayout } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import './auth-pages.css';

/**
 * Verify Email Sent Page
 *
 * Shown immediately after email/password sign-up.
 * Informs the user that a verification email has been sent and provides
 * a resend action in case the email did not arrive.
 *
 * This page is intentionally unguarded so both authenticated and
 * unauthenticated users can reach it.
 */

type PageState = 'idle' | 'resending' | 'resent' | 'error';

export function VerifyEmailSentPage() {
  const { resendVerificationEmail } = useAuth();

  const [pageState, setPageState] = React.useState<PageState>('idle');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleResend = async () => {
    setPageState('resending');
    setErrorMessage(null);

    try {
      await resendVerificationEmail();
      setPageState('resent');
    } catch (error) {
      const msg = error instanceof Error ? error.message : '';
      if (msg === 'NO_USER') {
        setErrorMessage('Your session has expired. Please sign in and try again.');
      } else if (msg.includes('too-many-requests')) {
        setErrorMessage('Too many requests. Please wait a moment before resending.');
      } else {
        setErrorMessage('Unable to resend verification email. Please try again.');
      }
      setPageState('error');
    }
  };

  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeader
          title="Check your email"
          subtitle="A verification link has been sent to your email address."
        />

        <div className="auth-form-container">
          <AuthMessage variant="info">
            Please verify your email before continuing. Check your inbox (and spam folder) for a
            message from Protoss.
          </AuthMessage>

          {pageState === 'resent' && (
            <AuthMessage variant="success">
              Verification email resent. Check your inbox.
            </AuthMessage>
          )}

          {pageState === 'error' && errorMessage && (
            <AuthMessage variant="error">{errorMessage}</AuthMessage>
          )}

          <Button
            variant="secondary"
            onClick={handleResend}
            disabled={pageState === 'resending' || pageState === 'resent'}
          >
            {pageState === 'resending' ? 'Resending…' : 'Resend verification email'}
          </Button>
        </div>

        <div className="auth-footer-text">
          <AuthFooterLink href="/signin">Back to sign in</AuthFooterLink>
        </div>
      </AuthCard>
    </AuthLayout>
  );
}
